// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import {
  type PartAttributeDefinition,
  type PartId,
  type PartDecision,
  PART_TYPE_ASSEMBLY_ID,
  type PartAttributeDefinitionVariantId,
} from '@evolvemep/api-ts';
import { Divider, Flex, Stack, Text, LoadingOverlay, ActionIcon, InputLabel } from '@mantine/core';
import { useForm } from '@mantine/form';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { ImageFromFile } from 'components/Mantine/FileUploads/ImageFromFile';
import { ModalButtons } from 'components/Mantine/Modals/ModalButtons';
import { useWrappedModal, WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { WrappedSelect } from 'components/Mantine/TypeSafeSelect/WrappedSelect';
import { hasLengthExactly } from 'helpers/helper-types';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { useBlockerWhen } from 'hooks/navigation/useBlockerWhen';
import { useIsMobile } from 'hooks/useIsMobile';
import { PartCard } from 'modules/Shop/CatalogSetup/PartCard';
import type {
  PartToAdd,
  PartToAddWithDecisions,
} from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/FromCatalog/CatalogPartAdder';

export type PartDecisionsToMake = PartToAdd & {
  partAttributes: PartAttributeDefinition[];
};

type Props = {
  title?: string;
  parts: PartDecisionsToMake[];
  onSubmit: (partsWithDecisions: PartToAddWithDecisions[]) => Promise<void>;
  onClose: () => void;
  opened?: boolean;
};

const DetailInfo = ({ label, value }: { label: string; value: ReactNode }) => (
  <Stack gap={4}>
    <InputLabel>{label}</InputLabel>
    <Text>{value || '--'}</Text>
  </Stack>
);

export const PartDecisionMakerForm = ({
  parts: allParts,
  onSubmit,
  onClose,
  expandedView,
}: Props & { expandedView: boolean; toggleExpandedView: () => void }) => {
  const isMobile = useIsMobile();
  const { setLoading } = useWrappedModal();
  const parts = useMemo(() => allParts.filter((p) => p.partAttributes.length > 0), [allParts]);

  const [saving, setSaving] = useState(false);
  useEffect(() => setLoading(saving), [setLoading, saving]);

  const [decisions, setDecisions] = useState<Record<PartId, PartDecision>>(
    parts.reduce((out, { part, partAttributes }) => {
      const dec: PartDecision = {
        partId: part.partId,
        decisions: partAttributes.map((a) => ({
          partAttributeDefinitionId: a.partAttributeDefinitionId,
          partAttributeDefinitionVariantId: hasLengthExactly(a.variants, 1)
            ? a.variants[0].partAttributeDefinitionVariantId
            : null,
          writeInValue: null,
        })),
      };
      return {
        ...out,
        [part.partId]: dec,
      };
    }, {}),
  );

  const [makingDecision, setMakingDecision] = useState(-1);
  const partToAdd = useMemo<PartDecisionsToMake | undefined>(() => parts[makingDecision], [makingDecision, parts]);
  const lastDecision = makingDecision + 1 >= parts.length;

  const form = useForm<PartDecision>({});

  const moveToDecision = useCallback(
    (newDecision: number, existingDecisions: typeof decisions) => {
      const part = parts[newDecision];
      if (isNotNil(part)) {
        setMakingDecision(newDecision);
        const partId = part.part.partId;
        if (existingDecisions[partId]) {
          form.setValues(existingDecisions[partId]);
        }
      }
    },
    [form, parts],
  );

  useEffect(() => {
    if (makingDecision === -1) {
      moveToDecision(0, decisions);
    }
  }, [makingDecision, moveToDecision, decisions]);

  const saveDecisions = async (values: typeof form.values) => {
    const newDecisions: typeof decisions = {
      ...decisions,
      [values.partId]: values,
    };
    setDecisions(newDecisions);
    if (lastDecision) {
      setSaving(true);
      await onSubmit(
        allParts.map((p) => ({
          ...p,
          decisions: newDecisions[p.part.partId]?.decisions,
        })),
      )
        .then(onClose)
        .finally(() => setSaving(false));
    } else {
      moveToDecision(makingDecision + 1, newDecisions);
    }
  };

  if (isNil(partToAdd)) return <LoadingOverlay visible />;

  return (
    <form
      onSubmit={form.onSubmit(saveDecisions)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
      }}
    >
      <Stack flex={1}>
        <Flex gap="lg" p="md">
          {expandedView && (
            <ImageFromFile
              fileId={partToAdd.part.image?.fileId}
              fallback={
                <EvolveIcon
                  icon={partToAdd.part.partTypeId === PART_TYPE_ASSEMBLY_ID ? 'Assembly' : 'Part'}
                  size="lg"
                  color="gray"
                />
              }
              mah={600}
            />
          )}
          <Flex direction="column" gap="md" flex={1} miw={isMobile ? undefined : 400}>
            {expandedView ? (
              <>
                <DetailInfo label="Name" value={partToAdd.part.name} />
                <DetailInfo label="Description" value={partToAdd.part.description} />
                <DetailInfo label="Manufacturer" value={partToAdd.part.manufacturerName} />
                <DetailInfo
                  label="Unit of Measure"
                  value={`${partToAdd.part.unitOfMeasureName} (${partToAdd.part.unitOfMeasureCode})`}
                />
              </>
            ) : (
              <PartCard part={partToAdd.part} fullWidth />
            )}
            <Divider label="Decisions" c="dimmed" />
            {partToAdd.partAttributes.map(({ allowWriteInVariant, variants, ...attribute }, i) => {
              const existing = form.values.decisions[i];
              if (isNil(existing)) return null;
              const { writeInValue, partAttributeDefinitionVariantId } = existing;

              const onCreate = (newWriteInValue: string) => {
                if (allowWriteInVariant && newWriteInValue.trim().length > 0) {
                  form.setFieldValue(`decisions.${i}`, {
                    partAttributeDefinitionId: attribute.partAttributeDefinitionId,
                    partAttributeDefinitionVariantId: null,
                    writeInValue: newWriteInValue.trim(),
                  });
                }
              };

              return (
                <WrappedSelect<PartAttributeDefinitionVariantId>
                  key={attribute.partAttributeDefinitionId}
                  label={
                    <Flex gap={4} align="center">
                      {attribute.name}
                      {allowWriteInVariant && (
                        <AgGridStyleTooltip label="Write-in values allowed" withArrow openDelay={200}>
                          <EvolveIcon icon="WriteInValue" size="sm" />
                        </AgGridStyleTooltip>
                      )}
                    </Flex>
                  }
                  data={variants.map((v) => ({
                    label: v.name,
                    value: v.partAttributeDefinitionVariantId,
                  }))}
                  searchable
                  clearable
                  value={partAttributeDefinitionVariantId}
                  creatable={allowWriteInVariant}
                  createdValue={writeInValue}
                  createLabel="Add write-in value"
                  onCreate={onCreate}
                  onChange={(newPartAttributeSelectVariantId) => {
                    form.setFieldValue(`decisions.${i}`, {
                      partAttributeDefinitionId: attribute.partAttributeDefinitionId,
                      partAttributeDefinitionVariantId: newPartAttributeSelectVariantId,
                      writeInValue: null,
                    });
                  }}
                  disabled={saving}
                />
              );
            })}
            {partToAdd.partAttributes.length === 0 && (
              <Text fz="sm" c="dimmed" ta="center">
                No decisions to make.
              </Text>
            )}
          </Flex>
        </Flex>
      </Stack>
      <ModalButtons
        confirmationText={lastDecision ? 'Submit' : `Next (${makingDecision + 1}/${parts.length})`}
        cancellationText={makingDecision === 0 ? 'Cancel' : 'Back'}
        type="submit"
        loading={saving}
        disabled={!form.isValid() || parts.length === 0}
        onClose={makingDecision === 0 ? onClose : () => moveToDecision(makingDecision - 1, decisions)}
      />
    </form>
  );
};

export const PartDecisionMakerModal = (props: Props) => {
  const isMobile = useIsMobile();
  const [expandedView, setExpandedView] = useState(false);

  const opened = props.opened ?? props.parts.length > 0;

  useBlockerWhen('decision-maker', opened);

  return (
    <WrappedModal
      title={props.title ?? 'Add catalog items'}
      opened={opened}
      onClose={props.onClose}
      closeOnConfirm={false}
      bodyProps={{ p: 0 }}
      hideModalButtons
      {...(isMobile
        ? { tall: true }
        : {
            size: 'auto',
            headerRightComponent: (
              <ActionIcon variant="light" onClick={() => setExpandedView((e) => !e)}>
                <EvolveIcon icon={expandedView ? 'CollapsePane' : 'ExpandPane'} />
              </ActionIcon>
            ),
          })}
    >
      <PartDecisionMakerForm
        {...props}
        expandedView={expandedView}
        toggleExpandedView={() => setExpandedView((e) => !e)}
      />
    </WrappedModal>
  );
};
